import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API, withCredentials: true, timeout: 60000 });

// Checkout calls get a shorter, explicit timeout so the "Continue to Paystack"
// spinner can never hang indefinitely — a slow/failed init surfaces a clear error.
const CHECKOUT_CFG = { timeout: 25000 };

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function apiError(e) {
  return formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Request failed";
}

// Friendly message for checkout failures (timeouts, network, server errors) so the
// user never sees a raw axios timeout string or an endless spinner.
export function checkoutError(e) {
  if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || ""))
    return "Paystack checkout is taking too long to start. Please check your connection and try again.";
  if (!e?.response) return "Could not reach the server to start Paystack checkout. Please try again.";
  return formatApiErrorDetail(e?.response?.data?.detail) || "Could not start Paystack checkout. Please try again.";
}

// Resource helpers
const crud = (base) => ({
  list: () => http.get(base).then((r) => r.data),
  get: (id) => http.get(`${base}/${id}`).then((r) => r.data),
  create: (body) => http.post(base, body).then((r) => r.data),
  update: (id, body) => http.put(`${base}/${id}`, body).then((r) => r.data),
  remove: (id) => http.delete(`${base}/${id}`).then((r) => r.data),
});

export const customers = crud("/customers");
export const products = crud("/products");
export const invoices = crud("/invoices");
export const quotes = crud("/quotes");
export const employees = crud("/employees");
export const payslips = crud("/payslips");
export const payments = crud("/payments");
export const recurring = crud("/recurring");

export const completeOnboarding = (b) => http.post("/onboarding/complete", b).then((r) => r.data);
export const getSettings = () => http.get("/settings").then((r) => r.data);
export const saveSettings = (b) => http.put("/settings", b).then((r) => r.data);
export const getDashboard = () => http.get("/dashboard").then((r) => r.data);
export const getReports = (start, end) =>
  http.get("/reports", { params: { start, end } }).then((r) => r.data);

export const logoUrl = () => `${API}/settings/logo?t=${Date.now()}`;
export const uploadLogo = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const removeLogo = () => http.delete("/settings/logo").then((r) => r.data);

export const invoicePdfUrl = (id) => `${API}/invoices/${id}/pdf?download=1`;
export const quotePdfUrl = (id) => `${API}/quotes/${id}/pdf?download=1`;
export const payslipPdfUrl = (id) => `${API}/payslips/${id}/pdf?download=1`;
export const customerStatementPdfUrl = (id, from, to) => {
  const q = new URLSearchParams({ download: "1" });
  if (from) q.set("date_from", from);
  if (to) q.set("date_to", to);
  return `${API}/customers/${id}/statement/pdf?${q.toString()}`;
};
export const emailStatement = (cid, body = {}) => http.post(`/customers/${cid}/statement/email`, body).then((r) => r.data);
export const receiptPdfUrl = (ref) => `${API}/billing/payments/${ref}/receipt?download=1`;

export const sendInvoiceEmail = (id) => http.post(`/invoices/${id}/send-email`).then((r) => r.data);
export const sendQuoteEmail = (id) => http.post(`/quotes/${id}/send-email`).then((r) => r.data);
export const setInvoiceStatus = (id, status) => http.put(`/invoices/${id}/status`, { status }).then((r) => r.data);
export const listReminders = () => http.get("/reminders").then((r) => r.data);
export const recurringAction = (id, action) => http.put(`/recurring/${id}/status`, { action }).then((r) => r.data);
export const recurringGenerateNow = (id) => http.post(`/recurring/${id}/generate-now`).then((r) => r.data);

// Billing
export const getBillingConfig = () => http.get("/billing/config").then((r) => r.data);
export const getSubscription = () => http.get("/billing/subscription").then((r) => r.data);
export const getBillingHistory = () => http.get("/billing/history").then((r) => r.data);
export const trialInit = (plan, billing_cycle) => http.post("/billing/trial/init", { plan, billing_cycle, callback_url: `${window.location.origin}/payment/callback` }, CHECKOUT_CFG).then((r) => r.data);
export const subscribeInit = (plan, billing_cycle) => http.post("/billing/subscribe", { plan, billing_cycle, callback_url: `${window.location.origin}/payment/callback` }, CHECKOUT_CFG).then((r) => r.data);
export const verifyPayment = (ref) => http.get(`/billing/verify/${ref}`).then((r) => r.data);
export const cancelSubscription = () => http.post("/billing/cancel").then((r) => r.data);
export const changePlan = (plan, billing_cycle) => http.post("/billing/change-plan", { plan, billing_cycle }).then((r) => r.data);

// Work Reports
export const workReports = crud("/work-reports");
export const reportStats = () => http.get("/work-reports-stats").then((r) => r.data);
export const reportPhotoUrl = (rid, pid) => `${API}/work-reports/${rid}/photos/${pid}?t=${Date.now()}`;
export const reportPdfUrl = (rid) => `${API}/work-reports/${rid}/pdf`;
export const reportPdfDownloadUrl = (rid) => `${API}/work-reports/${rid}/pdf?download=1`;
export const uploadReportPhoto = (rid, file, caption = "", figure = "", sectionId = "") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("caption", caption);
  fd.append("figure", figure);
  fd.append("section_id", sectionId);
  return http.post(`/work-reports/${rid}/photos?caption=${encodeURIComponent(caption)}&figure=${encodeURIComponent(figure)}&section_id=${encodeURIComponent(sectionId)}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteReportPhoto = (rid, pid) => http.delete(`/work-reports/${rid}/photos/${pid}`).then((r) => r.data);
export const updateReportPhoto = (rid, pid, body) => http.put(`/work-reports/${rid}/photos/${pid}`, body).then((r) => r.data);
export const emailReport = (rid) => http.post(`/work-reports/${rid}/email`).then((r) => r.data);
export const duplicateReport = (rid) => http.post(`/work-reports/${rid}/duplicate`).then((r) => r.data);

// Team & workspace
export const getTeam = () => http.get("/team").then((r) => r.data);
export const inviteMember = (email, role) => http.post("/team/invite", { email, role }).then((r) => r.data);
export const revokeInvite = (id) => http.post(`/team/invitations/${id}/revoke`).then((r) => r.data);
export const resendInvite = (id) => http.post(`/team/invitations/${id}/resend`).then((r) => r.data);
export const updateMemberRole = (uid, role) => http.put(`/team/members/${uid}/role`, { role }).then((r) => r.data);
export const removeMember = (uid) => http.delete(`/team/members/${uid}`).then((r) => r.data);
export const getInvite = (token) => http.get(`/team/invite/${token}`).then((r) => r.data);
export const acceptInvite = (token, name, password) => http.post(`/team/invite/${token}/accept`, { name, password }).then((r) => r.data);
export const getAuditLogs = () => http.get("/audit-logs").then((r) => r.data);

// API keys (Pro)
export const getApiKeys = () => http.get("/keys").then((r) => r.data);
export const createApiKey = (name) => http.post("/keys", { name }).then((r) => r.data);
export const rotateApiKey = (id) => http.post(`/keys/${id}/rotate`).then((r) => r.data);
export const revokeApiKey = (id) => http.post(`/keys/${id}/revoke`).then((r) => r.data);
export const getApiUsage = () => http.get("/keys/usage").then((r) => r.data);

// Plumbing Operations (Business/Pro)
export const plumbingOverview = () => http.get("/plumbing/overview").then((r) => r.data);
// Supplier directory (per-business, isolated)
export const listSuppliers = (params = {}) => http.get("/plumbing/suppliers", { params }).then((r) => r.data);
export const createSupplier = (body) => http.post("/plumbing/suppliers", body).then((r) => r.data);
export const updateSupplier = (id, body) => http.put(`/plumbing/suppliers/${id}`, body).then((r) => r.data);
export const deleteSupplier = (id) => http.delete(`/plumbing/suppliers/${id}`).then((r) => r.data);
export const discoverSuppliers = (params = {}) => http.get("/plumbing/suppliers/discover", { params, timeout: 60000 }).then((r) => r.data);
export const listTeams = () => http.get("/plumbing/teams").then((r) => r.data);
export const createTeam = (body) => http.post("/plumbing/teams", body).then((r) => r.data);
export const updateTeam = (id, body) => http.put(`/plumbing/teams/${id}`, body).then((r) => r.data);
export const deleteTeam = (id) => http.delete(`/plumbing/teams/${id}`).then((r) => r.data);
export const listFieldEmployees = () => http.get("/plumbing/employees").then((r) => r.data);
export const inviteFieldEmployee = (body) => http.post("/plumbing/employees/invite", body).then((r) => r.data);
export const updateFieldEmployee = (id, body) => http.put(`/plumbing/employees/${id}`, body).then((r) => r.data);
export const removeFieldEmployee = (id) => http.delete(`/plumbing/employees/${id}`).then((r) => r.data);
export const fieldMe = () => http.get("/plumbing/me").then((r) => r.data);

// Plumbing add-on billing
export const getAddons = () => http.get("/billing/addons").then((r) => r.data);
export const subscribeAddon = (billing_cycle) => http.post("/billing/addons/plumbing/subscribe", { billing_cycle, callback_url: `${window.location.origin}/payment/callback` }, CHECKOUT_CFG).then((r) => r.data);
export const cancelAddon = () => http.post("/billing/addons/plumbing/cancel").then((r) => r.data);

// Plumbing Stock Orders (Phase B)
export const listStockOrders = (params = {}) => http.get("/plumbing/stock-orders", { params }).then((r) => r.data);
export const getStockOrder = (id) => http.get(`/plumbing/stock-orders/${id}`).then((r) => r.data);
export const createStockOrder = (body) => http.post("/plumbing/stock-orders", body).then((r) => r.data);
export const updateStockOrder = (id, body) => http.put(`/plumbing/stock-orders/${id}`, body).then((r) => r.data);
export const deleteStockOrder = (id) => http.delete(`/plumbing/stock-orders/${id}`).then((r) => r.data);
export const whatsappStockOrder = (id) => http.post(`/plumbing/stock-orders/${id}/whatsapp`).then((r) => r.data);
export const markStockOrderSent = (id) => http.post(`/plumbing/stock-orders/${id}/mark-sent`).then((r) => r.data);
export const updateStockOrderStatus = (id, body) => http.put(`/plumbing/stock-orders/${id}/status`, body).then((r) => r.data);
export const stockOrderPdfUrl = (id) => `${API}/plumbing/stock-orders/${id}/pdf?download=1`;

// Plumbing Jobs (Phase C)
export const listJobs = (params = {}) => http.get("/plumbing/jobs", { params }).then((r) => r.data);
export const getJob = (id) => http.get(`/plumbing/jobs/${id}`).then((r) => r.data);
export const jobsOverview = () => http.get("/plumbing/jobs-overview").then((r) => r.data);
export const createJob = (body) => http.post("/plumbing/jobs", body).then((r) => r.data);
export const updateJob = (id, body) => http.put(`/plumbing/jobs/${id}`, body).then((r) => r.data);
export const deleteJob = (id) => http.delete(`/plumbing/jobs/${id}`).then((r) => r.data);
export const updateJobStatus = (id, body) => http.put(`/plumbing/jobs/${id}/status`, body).then((r) => r.data);
export const setJobMaterials = (id, materials) => http.post(`/plumbing/jobs/${id}/materials`, { materials }).then((r) => r.data);
export const jobPhotoUrl = (jid, pid) => `${API}/plumbing/jobs/${jid}/photos/${pid}?t=${Date.now()}`;
export const jobStockOrders = (jid) => http.get(`/plumbing/jobs/${jid}/stock-orders`).then((r) => r.data);
export const uploadJobPhoto = (jid, file, caption = "") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("caption", caption);
  return http.post(`/plumbing/jobs/${jid}/photos?caption=${encodeURIComponent(caption)}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteJobPhoto = (jid, pid) => http.delete(`/plumbing/jobs/${jid}/photos/${pid}`).then((r) => r.data);

// Plumbing Monthly Team Reports (Phase C)
export const listMonthlyReports = () => http.get("/plumbing/reports/monthly").then((r) => r.data);
export const generateMonthlyReport = (period) => http.post("/plumbing/reports/monthly/generate", { period }).then((r) => r.data);
export const monthlyReportPdfUrl = (id) => `${API}/plumbing/reports/monthly/${id}/pdf?download=1`;
export const resendMonthlyReport = (id) => http.post(`/plumbing/reports/monthly/${id}/resend`).then((r) => r.data);

// Client Share Links (secure, no-login public document viewing)
export const createShareLink = (doc_type, doc_id) => http.post("/share", { doc_type, doc_id }).then((r) => r.data);
export const listShareLinks = (params = {}) => http.get("/share", { params }).then((r) => r.data);
export const revokeShareLink = (id) => http.post(`/share/${id}/revoke`).then((r) => r.data);
export const publicShareMeta = (token) => http.get(`/public/share/${token}`).then((r) => r.data);
export const publicSharePdfUrl = (token, download = false) => `${API}/public/share/${token}/pdf${download ? "?download=1" : ""}`;
